// #144 — the ONE shared, React-free formatter for a contribution's election designation at
// every read boundary (ledger, report detail/summary, triage summary, resolution chain).
// Sibling of `moneyMovementLabel` / `importSourceLabel`: a pure function reused across packages
// and cheaply unit-tested.
//
// The display asymmetry is deliberate (see the ticket): the PICKER always shows a year to
// disambiguate its options, but on READ the year renders ONLY for an OFF-CYCLE gift. So this
// formatter keys off the year being null:
//   - null / undefined year → the plain designation ("General")
//   - a set year on Primary/General → "General 2026" (the off-cycle signal, made visible)
//   - Undesignated → "Undesignated" regardless of any (inert) year
// Because it degrades to the plain designation when the year is null, Alabama / Virginia read
// surfaces (where the year is always null) render byte-identically to before this shipped.

import { ElectionDesignation } from '../generated/enums.gen';

/**
 * Resolves the display label for a contribution's election designation.
 *
 * @param designation the raw wire designation string (`'Primary' | 'General' | 'Undesignated'`,
 *   or any future/unknown value, which passes through unchanged — never empty, never throws).
 * @param electionYear the off-cycle year, or null/undefined for the campaign's own current cycle.
 */
export function electionDesignationLabel(
  designation: string,
  electionYear?: number | null,
): string {
  // An Undesignated gift carries no election code — a year on it is inert, so never show one.
  if (designation === ElectionDesignation.Undesignated) {
    return designation;
  }
  // Own-cycle (null) stays plain; an off-cycle year is appended so it's visible on every surface.
  return electionYear == null ? designation : `${designation} ${electionYear}`;
}
